/*
 * TYOHAAR "Load more".
 *
 * Fetches the next page through the Section Rendering API and appends its
 * product cards to the grid already on screen. Nothing about paging is
 * reinvented: the URL comes from Shopify's paginate object and already carries
 * the active filters and sort, so page size, filtering and sorting keep
 * working through the theme's own code.
 *
 * The count and the bar are derived from the grid itself rather than kept in a
 * counter, so they cannot drift out of step with what is actually rendered.
 */
if (!customElements.get('tyoh-load-more')) {
  customElements.define(
    'tyoh-load-more',
    class TyohLoadMore extends HTMLElement {
      connectedCallback() {
        this.button = this.querySelector('[data-load-more]');
        this.countEl = this.querySelector('[data-load-count]');
        this.bar = this.querySelector('[data-load-bar]');
        this.grid = document.getElementById('product-grid');
        this.total = Number(this.dataset.total) || 0;
        this.template = this.dataset.countTemplate || 'Showing {shown} of {total}';
        if (!this.grid) return;

        // Every URL this element has already asked for. A response that
        // repeats one — or that arrives twice — must not append anything.
        this.fetched = new Set();

        this.render();
        if (this.button) this.button.addEventListener('click', this.onClick.bind(this));
      }

      get shown() {
        return this.grid.querySelectorAll('.grid__item').length;
      }

      /* The ids already on screen, so an item can never be added twice. */
      get renderedIds() {
        return new Set(
          Array.from(this.grid.querySelectorAll('.grid__item')).map((item) => item.dataset.productId)
        );
      }

      finish() {
        if (this.button) this.button.hidden = true;
        this.setAttribute('data-complete', '');
      }

      render() {
        // Capped: a stray duplicate should never be able to print "67 of 39".
        const shown = this.total > 0 ? Math.min(this.shown, this.total) : this.shown;
        if (this.countEl) {
          this.countEl.textContent = this.template
            .replace('{shown}', shown)
            .replace('{total}', this.total);
        }
        if (this.bar) {
          const progress = this.total > 0 ? Math.min(shown / this.total, 1) : 0;
          this.bar.style.setProperty('--tload-progress', progress);
        }
        // Nothing left to ask for, whatever the next link happens to say.
        if (this.total > 0 && this.shown >= this.total) this.finish();
      }

      setLoading(isLoading) {
        this.toggleAttribute('data-loading', isLoading);
        if (!this.button) return;
        this.button.setAttribute('aria-disabled', isLoading ? 'true' : 'false');
        this.button.querySelector('.loading__spinner')?.classList.toggle('hidden', !isLoading);
      }

      /*
       * Dawn's reveal-on-scroll observer is set up once, at load, so cards
       * appended later would never be observed and would sit at opacity 0
       * forever. Stripping the animation classes is the reliable fix — the new
       * rows simply appear, which is what the design shows anyway.
       */
      static clean(item) {
        item.classList.remove('scroll-trigger', 'animate--slide-in', 'scroll-trigger--offscreen');
        item.removeAttribute('data-cascade');
        item.style.removeProperty('--animation-order');
        return item;
      }

      async onClick(event) {
        event.preventDefault();
        if (this.loading) return;

        const url = this.button.getAttribute('href');
        if (!url) return;

        // Asked for already: the chain is going in circles, so stop rather
        // than append the same products a second time.
        if (this.fetched.has(url)) {
          this.finish();
          return;
        }
        this.fetched.add(url);

        this.loading = true;
        this.setLoading(true);

        try {
          // Built through URL so an existing section_id is replaced rather
          // than appended a second time.
          const target = new URL(url, window.location.origin);
          target.searchParams.set('section_id', this.dataset.sectionId);

          const response = await fetch(target);
          if (!response.ok) throw new Error(`Load more failed: ${response.status}`);

          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const items = doc.querySelectorAll('#product-grid .grid__item');
          if (!items.length) throw new Error('Load more returned no products');

          const seen = this.renderedIds;
          const fragment = document.createDocumentFragment();
          let added = 0;
          items.forEach((item) => {
            const id = item.dataset.productId;
            // No id means an older cached section; fall back to appending it
            // rather than dropping a product the shopper should see.
            if (id && seen.has(id)) return;
            if (id) seen.add(id);
            fragment.appendChild(TyohLoadMore.clean(document.importNode(item, true)));
            added += 1;
          });

          if (added > 0) this.grid.appendChild(fragment);

          // The fetched page knows where the one after it is — or has no
          // button at all, which is how we learn we have reached the end.
          // Nothing new in the response means the same thing.
          const nextHref = doc.querySelector('[data-load-more]')?.getAttribute('href');
          if (nextHref && added > 0 && !this.fetched.has(nextHref)) {
            this.button.setAttribute('href', nextHref);
          } else {
            this.finish();
          }

          this.render();
        } catch (error) {
          // Rather than leave a dead button, fall back to what the link says.
          console.error('[load-more]', error);
          window.location = url;
        } finally {
          this.loading = false;
          this.setLoading(false);
        }
      }
    }
  );
}
